import { Controller } from 'stimulus'

// Bulk Update forms: the results grid's Fill column
// (jobs/v2_sample_result_field_values/_set_multiple_form) and the samples
// table's (jobs/v2_samples/_set_multiple_form). Posts the form and closes the
// modal once the fill is queued.
//
// On the results grid of a job that logs changed values, a fill that
// overwrites saved values needs one reason for change. sync() works out how
// many saved values the current choices change (from the counts the form
// carries), and shows the reason box only when that's more than none. The
// samples form has none of these targets and only posts.
const STATUSES_WITHOUT_REASON = ['', 'not tested', 'in progress']

export default class extends Controller {
  static targets = ['form', 'reason', 'reasonInput', 'reasonHint', 'quiet', 'error', 'submitButton']

  connect() {
    this.sync()
  }

  sync() {
    if (!this.data.has('kind')) return

    const needed = this.reasonNeeded()
    if (this.hasReasonTarget && !this.forced) {
      this.reasonTarget.hidden = needed === 0
      this.reasonInputTarget.disabled = needed === 0
      if (this.hasReasonHintTarget) this.reasonHintTarget.textContent = this.reasonHint(needed)
    }
    if (this.hasQuietTarget) this.quietTarget.hidden = this.overwriteAll()
  }

  // How many saved values the fill would change; mirrors what the bulk
  // services log (BulkUpdateResultService, BulkUpdateSampleResultFieldValuesService).
  reasonNeeded() {
    if (!this.hasReasonTarget || this.data.get('records') !== 'true') return 0

    const kind = this.data.get('kind')
    const value = this.field('set_multiple_value')
    const over = this.field('overwrite_status_value')

    if (kind === 'status') {
      return Object.entries(this.json('statusCounts')).reduce((sum, [status, count]) => {
        if (STATUSES_WITHOUT_REASON.includes(status || '')) return sum
        if (over && over !== 'all' && over !== status) return sum
        if (value !== 'automatic' && status === value) return sum
        return sum + count
      }, 0)
    }

    if (kind === 'dropdown') {
      const counts = this.json('valueCounts')
      if (over && over !== 'all') return over === value ? 0 : (counts[over] || 0)
      if (!this.overwriteAll()) return 0
      return Number(this.data.get('filled') || 0) - (counts[value] || 0)
    }

    return this.overwriteAll() ? Number(this.data.get('filled') || 0) : 0
  }

  reasonHint(needed) {
    if (this.data.get('kind') === 'status') {
      if (this.field('set_multiple_value') === 'automatic') {
        return 'Saved on each status the recompute changes. Rows still Not tested or In progress don’t need one.'
      }
      return `Saved on each of the ${needed} ${needed === 1 ? 'status' : 'statuses'} this changes. Rows still Not tested or In progress don’t need one.`
    }
    return `Saved on each of the ${needed} saved ${needed === 1 ? 'value' : 'values'} this replaces, so none of them waits for a reason.`
  }

  overwriteAll() {
    const checked = this.formTarget.querySelector('input[name="overwrite_non_blank"]:checked')
    return !!checked && checked.value === 'true'
  }

  field(name) {
    const el = this.formTarget.querySelector(`[name="${name}"]`)
    return el ? el.value.trim() : ''
  }

  json(key) {
    try {
      return JSON.parse(this.data.get(key) || '{}')
    } catch {
      return {}
    }
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.hidden = true
    if (this.hasReasonInputTarget) this.reasonInputTarget.classList.remove('is-invalid')
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  // The server counted saved values the form's counts missed: open the
  // reason box and keep it open.
  askForReason() {
    if (!this.hasReasonTarget) return
    this.forced = true
    this.reasonTarget.hidden = false
    this.reasonInputTarget.disabled = false
    if (this.hasReasonHintTarget) this.reasonHintTarget.textContent = 'Saved on each saved value this changes, so none of them waits for a reason.'
    this.reasonInputTarget.classList.add('is-invalid')
    this.reasonInputTarget.focus()
  }

  async submit(event) {
    event.preventDefault()

    const form = this.formTarget
    const { url } = form.dataset

    if (this.hasReasonInputTarget && !this.reasonInputTarget.disabled && !this.reasonInputTarget.value.trim()) {
      this.reasonInputTarget.classList.add('is-invalid')
      this.reasonInputTarget.focus()
      if (this.hasErrorTarget) this.showError('Enter a reason for change. It’s saved on each saved value this changes.')
      return
    }

    const formData = new FormData(form)
    this.busy(true)

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: formData
      })

      const text = await response.text()
      let data

      try {
        data = JSON.parse(text)
      } catch {
        data = null
      }

      const refused = !response.ok && data?.errors?.length && this.hasErrorTarget
      if (data && data.toastr && window.toastr && !refused) {
        const { type = 'info', message, title } = data.toastr
        const fn = window.toastr[type] || window.toastr.info
        if (fn) fn(message, title)
      }

      if (response.ok) {
        document.dispatchEvent(new Event('modal:close'))
      } else if (refused) {
        // Refused (a reason it still needs): say so in the form and stay open.
        this.showError(data.errors.join(' '))
        if (data.needs_reason) this.askForReason()
      } else if (!data?.toastr && window.toastr) {
        window.toastr.error(`Request failed (status: ${response.status})${text ? `: ${text}` : ''}`)
      }
    } catch (error) {
      if (window.toastr) window.toastr.error(`Network error: ${error.message}`)
    } finally {
      this.busy(false)
    }
  }

  busy(on) {
    if (!this.hasSubmitButtonTarget) return
    this.submitButtonTarget.disabled = on
    this.submitButtonTarget.textContent = on ? 'Setting values…' : 'Set Values'
  }
}
